import React, { useId } from 'react';
import type { JSX, PropsWithChildren } from 'react';

import { transformChildNodeByType } from '@change/ds-core-ui';
import { ErrorMessage } from '@change/ds-forms';
import { cn } from '@change/ds-utils';

import { ToggleButton as ToggleButtonComponent } from './ToggleButton';

type ChangeEvent = React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>;
type ToggleButtonGroupProps<VALUE extends string | undefined> = PropsWithChildren<{
	ariaLabel: string;
	onChange: (e: ChangeEvent, value: VALUE) => void;
	value: VALUE;
	name: string;
	error?: string | null;
	containerClassName?: string;
}> &
	Omit<React.HTMLAttributes<HTMLFieldSetElement>, 'onChange'>;

export function ToggleButtonGroup<VALUE extends string | undefined>({
	ariaLabel,
	children,
	className,
	containerClassName,
	onChange,
	value,
	name,
	error,
	...rest
}: ToggleButtonGroupProps<VALUE>): JSX.Element {
	const validChildren = React.Children.toArray(children).filter((child) => React.isValidElement(child));
	const id = useId();
	const groupId = `${name}-${id}`;

	return (
		<fieldset
			className={cn('group p-0', className)}
			aria-label={ariaLabel}
			aria-invalid={!!error}
			aria-describedby={error ? `${groupId}-error` : undefined}
			{...rest}
		>
			<legend className="sr-only">{ariaLabel}</legend>

			<div className={cn('toggle-button-container', containerClassName)}>
				{transformChildNodeByType(validChildren, ToggleButtonComponent<VALUE>, (ToggleButton) =>
					React.cloneElement(ToggleButton, {
						onChange,
						selected: ToggleButton.props.value === value,
						value: ToggleButton.props.value,
					}),
				)}
			</div>

			<input type="hidden" name={name} value={value ?? ''} data-qa="toggle-button-group" />

			{error && (
				<div id={`${groupId}-error`} className="mt-2 flex space-x-4">
					<ErrorMessage>{error}</ErrorMessage>
				</div>
			)}
		</fieldset>
	);
}
